import { Share, Plus, CheckCircle } from 'lucide-react';
import { BottomSheet } from '@/components/BottomSheet';

interface InstallInstructionsProps {
  isOpen: boolean;
  onClose: () => void;
  onDismissBanner: () => void;
}

/**
 * iOS Safari Install Instructions
 *
 * Shows step-by-step instructions for adding the app to the Home Screen
 * in iOS Safari. Displayed as a bottom sheet.
 */
export function InstallInstructions({
  isOpen,
  onClose,
  onDismissBanner,
}: InstallInstructionsProps) {
  const handleDone = () => {
    onDismissBanner();
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Add to Home Screen"
      initialHeight={65}
      minHeight={40}
      maxHeight={85}
    >
      <div className="px-4 pb-6 space-y-6">
        {/* Introduction */}
        <p className="text-sm text-gray-600">
          Follow these steps to install Spirit of Shabbat on your device for the best experience:
        </p>

        {/* Steps */}
        <div className="space-y-4">
          {/* Step 1 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-[#f8e8f8] flex items-center justify-center">
              <span className="text-[#8e4585] font-bold">1</span>
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-900">Tap the Share button</h4>
                <div className="p-1 bg-blue-500 rounded">
                  <Share size={14} className="text-white" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Find it at the bottom of Safari (the square with an arrow pointing up)
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-[#f8e8f8] flex items-center justify-center">
              <span className="text-[#8e4585] font-bold">2</span>
            </div>
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-900">Tap "Add to Home Screen"</h4>
                <div className="p-1 bg-gray-200 rounded">
                  <Plus size={14} className="text-gray-700" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Scroll down in the share sheet if you don't see it right away
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-[#f8e8f8] flex items-center justify-center">
              <span className="text-[#8e4585] font-bold">3</span>
            </div>
            <div className="flex-1 pt-1">
              <h4 className="font-medium text-gray-900">Tap "Add"</h4>
              <p className="text-sm text-gray-500 mt-1">
                Confirm by tapping "Add" in the top right corner
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div className="flex-1 pt-1">
              <h4 className="font-medium text-gray-900">Open from Home Screen</h4>
              <p className="text-sm text-gray-500 mt-1">
                Find the Spirit of Shabbat icon on your Home Screen and tap to open
              </p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-[#fcf4fc] rounded-xl p-4 space-y-2">
          <h4 className="font-medium text-[#63345e] text-sm">Why install?</h4>
          <ul className="text-xs text-[#8e4585] space-y-1.5">
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-600 shrink-0" />
              Get push notifications for new messages
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-600 shrink-0" />
              Full-screen app experience
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-600 shrink-0" />
              Quick access from your Home Screen
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-600 shrink-0" />
              Works even with limited connectivity
            </li>
          </ul>
        </div>

        {/* Done button */}
        <button
          onClick={handleDone}
          className="w-full py-3 bg-[#8e4585] text-white rounded-xl font-medium hover:bg-[#7a3b73] transition-colors"
        >
          Got it!
        </button>
      </div>
    </BottomSheet>
  );
}
